import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import Dialog from "@mui/material/Dialog";
import CreateIcon from "@mui/icons-material/Create";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import useMediaQuery from "@mui/material/useMediaQuery";
import ImageIcon from "@mui/icons-material/Image";
import { Box, Button, DialogActions, DialogContent, Paper, Typography } from "@mui/material";

import DrawingBoard from "./DrawingBoard";
import { tl, t } from "../../components/translate";
import DebouncedTextField from "../../components/DebouncedTextField";
import useAssessmentLabel from "../../hooks/useAssessmentLabel";
import useCurrentResourceCentre from "../../hooks/useCurrentResourceCentre";
import { getAssessmentTemplateImages } from "../../actions/assessments";
import { IThunkDispatch } from "../../store";
import { AttachmentInterface } from "../../interfaces/AttachmentInterface";

// @ts-expect-error - css
import styles from "./Assessment.module.css";

const MAX_LENGTH = 2000;

interface StatusProps {
  status: string;
  assessmentImage: string;
  onChange: (status: string) => void;
  onChangeImage: (image: string) => void;
  isOpd?: boolean;
}

const Status: React.FC<StatusProps> = ({
  status,
  assessmentImage,
  onChange,
  onChangeImage,
  isOpd = true
}) => {
  const theme = useTheme();
  const assessmentLabels = useAssessmentLabel();
  const dispatch = useDispatch<IThunkDispatch>();
  const currentResourceCentre = useCurrentResourceCentre();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // local states
  const [image, setImage] = useState("");
  const [boardData, setBoardData] = useState([]);
  const [bgImageIndex, setbgImageIndex] = useState(0);
  const [localStatus, setLocalStatus] = useState(status || "");
  const [viewImage, setViewImage] = React.useState(false);
  const [openDrawingDialog, setOpenDrawingDialog] = useState(false);

  // variables
  const rcTemplateImages: AttachmentInterface[] =
    currentResourceCentre?.assessmentTemplateImages || [];

  // effects
  useEffect(() => {
    if (!rcTemplateImages?.length) {
      dispatch(getAssessmentTemplateImages());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentResourceCentre?.id]);

  useEffect(() => {
    setLocalStatus(status || "");
    setImage(assessmentImage);
  }, [assessmentImage, status]);

  useEffect(() => {
    onChangeImage(image);
  }, [image]);

  return (
    <Box className={styles.assessmentRow} height="auto">
      <Box className={styles.assessmentLabel}>
        <Typography component="span" fontWeight="600">
          {isOpd ? assessmentLabels.examination : tl("assessment.status")}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          width: "100%",
          alignItems: "center"
        }}
      >
        <Box className={styles.assessmentField} sx={{ flexGrow: 1 }}>
          <DebouncedTextField
            margin="dense"
            id="outlined-basic"
            variant="outlined"
            data-testmation="examinationTextField"
            fullWidth
            multiline
            value={localStatus}
            minRows={3}
            maxRows={3}
            placeholder={t("assessment.typeStatusHere")}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { maxLength: MAX_LENGTH },
              formHelperText: { style: { position: "absolute", right: 0, bottom: 0, margin: 5 } },
              input: {
                endAdornment: (
                  <Box
                    title="Draw image"
                    data-testmation="pencilButton"
                    sx={{
                      position: "absolute",
                      top: "4px",
                      right: "4px",
                      border: "1px solid grey",
                      width: "30px",
                      height: "30px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: "lightgrey",
                      borderRadius: "2px",
                      cursor: "pointer"
                    }}
                    onClick={() => setOpenDrawingDialog(true)}
                  >
                    <ImageIcon />
                  </Box>
                )
              }
            }}
            onChange={(e) => {
              setLocalStatus(e.target.value);
              onChange(e.target.value);
            }}
            debounceAt={400}
            style={{ padding: 0 }}
            helperText={`${localStatus.length}/${MAX_LENGTH}`}
          />
        </Box>

        {image && (
          <Box sx={{ width: "70px", display: "flex", gap: 0.5, flexDirection: "column" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <CloseIcon
                sx={{
                  cursor: "pointer"
                }}
                onClick={() => {
                  setViewImage(false);
                  setImage("");
                  setBoardData([]);
                }}
                data-testmation="examinationOpenImageModalButton"
              />
              <CreateIcon
                sx={{
                  cursor: "pointer"
                }}
                onClick={() => setOpenDrawingDialog(true)}
              />
            </Box>
            <Box
              sx={{
                cursor: "pointer",
                px: 0.5
              }}
              component={Paper}
              onClick={() => setViewImage(true)}
            >
              <img
                src={image}
                style={{ margin: "auto", height: "50px", width: "50px" }}
                alt="assessment"
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Image View Dialog */}
      <Dialog open={viewImage} onClose={() => setViewImage(false)}>
        <DialogContent>
          <img src={image} alt="assessment" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewImage(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Drawing Board Dialog */}
      <Dialog open={openDrawingDialog} classes={{ paper: "slate" }} fullScreen={fullScreen}>
        <DrawingBoard
          closeModal={() => {
            setOpenDrawingDialog(false);
          }}
          title={tl("assessment.statusDescription")}
          handleImageSave={(img, drawingBoardData, imageIndex) => {
            setImage(img);
            setBoardData(drawingBoardData);
            setbgImageIndex(imageIndex);
          }}
          width={fullScreen ? window.innerWidth - 32 : 500}
          height={fullScreen ? window.innerHeight - 180 : 500}
          boardData={boardData}
          setBoardData={setBoardData}
          imageIndex={bgImageIndex}
          setImageIndex={setbgImageIndex}
          isAssessmentForm
          buttonGroupSize="medium"
          rcTemplateImages={rcTemplateImages}
        />
      </Dialog>
    </Box>
  );
};

export default Status;
